import React, { useContext, useEffect, useMemo, useState } from "react";
import { Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useMutation } from "react-query";
import GlobalContext from "src/context/global-context";
import client from "../../api";
import {
  APISomethingWrongMsg,
  LIST_PAGE_SIZE,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import { getErrorMessage } from "src/lib/utils";
import { useNavigate } from "react-router-dom";

interface OrderedCard {
  id: number;
  masked_card_number: string | null;
  processing_status: string;
  card_status: string;
  createdAt: string;
  residency_type: "RESIDENT" | "NON_RESIDENT";
  asset?: {
    code: string;
  };
  card_type?: {
    name: string;
  };
  user?: {
    email: string;
    id: number;
  };
  user_data?: {
    first_name: string;
    last_name: string;
  };
}

interface OrderedCardsResponse {
  total_records: number;
  total_pages: number;
  current_page: number;
  per_page: number;
  data: OrderedCard[];
}

const CARD_STATUS_OPTIONS = [
  "active",
  "inactive",
  "close",
  "block",
  "unblock",
  "deactivated",
];

const VirtualOrderedCardsList = () => {
  const { setAlertText, setIsAlertOpened } = useContext(GlobalContext);
  const isMounted = useIsMounted();

  const { canView, canList } = usePermissions(
    permissionConstants.ORDERED_CARDS_HISTORY
  );
  const canViewOrderedCards = canView || canList;

  const [tableData, setTableData] = useState<OrderedCard[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: LIST_PAGE_SIZE,
    total: 0,
  });

  type FilterType =
    | "user_id"
    | "card_status"
    | "masked_card_number"
    | "card_type";

  const [selectedFilter, setSelectedFilter] = useState<FilterType>("user_id");
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [residencyType, setResidencyType] = useState("all");
  const navigate = useNavigate();
  const [products, setProducts] = useState<any[]>([]);

  const onSelectUser = (userID: number) => {
    navigate(`/users/${String(userID)}`, {
      replace: true,
    });
  };

  const columns: ColumnsType<OrderedCard> = useMemo(
    () => [
      {
        title: "User ID",
        dataIndex: ["user", "id"],
        key: "id",
        render: (value) => (
          <div className="flex flex-row gap-2 items-center">
            <a
              className="font-manrope text-secondary text-[14px] font-medium underline hover:text-primary hover:underline"
              onClick={() => onSelectUser(value)}
            >
              {value}
            </a>
          </div>
        ),
      },
      {
        title: "Full Name",
        key: "fullName",
        render: (_, record) =>
          `${record.user_data?.first_name ?? ""} ${
            record.user_data?.last_name ?? ""
          }`,
      },
      {
        title: "Email",
        dataIndex: ["user", "email"],
        key: "email",
      },
      {
        title: "Currency",
        render: (_, record) => record.asset?.code?.toUpperCase() || "-",
      },
      {
        title: "Card Type",
        render: (_, record) => record.card_type?.name || "-",
      },
      {
        title: "Residency Type",
        dataIndex: "residency_type",
        key: "residency_type",
        render: (value?: string) => value?.trim() || "—",
      },
      {
        title: "Card Status",
        dataIndex: "card_status",
        key: "card_status",
      },

      {
        title: "Card Number",
        dataIndex: "masked_card_number",
        key: "masked_card_number",

        render: (value) => value || "-",
      },
      {
        title: "Processing Status",
        dataIndex: "processing_status",
        key: "processing_status",
      },
      {
        title: "Created At",
        dataIndex: "createdAt",
        key: "createdAt",
        render: (value) => new Date(value).toLocaleString(),
      },
    ],
    [],
  );

  const { mutate: getProducts } = useMutation(client.cardProducts.list, {
    onSuccess: (res: any) => {
      setProducts(res?.data?.data || []);
    },
  });

  useEffect(() => {
    if (canViewOrderedCards) {
      getProducts({});
    }
  }, [canViewOrderedCards]);

  const { mutate: getAllOrderedCardsList, isLoading } = useMutation(
    client.cardManagementOtherApis.getAllOrderedCardsList,

    {
      onSuccess: (res: any) => {
        const response: OrderedCardsResponse = res?.data;

        setTableData(response?.data || []);

        setPagination((prev) => ({
          ...prev,

          total: response?.total_records || 0,
        }));
      },

      onError: (error: any) => {
        if (error?.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else if (error?.response) {
          setAlertText(getErrorMessage(error.response.data));
        } else {
          setAlertText(APISomethingWrongMsg);
        }

        setIsAlertOpened(true);
      },
    },
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue);
      setPagination((prev) => ({
        ...prev,
        page: 1,
      }));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchValue]);

  useEffect(() => {
    if (!isMounted || !canViewOrderedCards) return;

    const payload: any = {
      page: pagination.page,
      limit: pagination.limit,
    };

    if (debouncedSearch.trim()) {
      payload[selectedFilter] =
        selectedFilter === "user_id"
          ? Number(debouncedSearch)
          : debouncedSearch;
    }
    if (residencyType !== "all") {
      payload.residency_type = residencyType;
    }
    getAllOrderedCardsList(payload);
  }, [
    isMounted,
    canViewOrderedCards,
    pagination.page,
    debouncedSearch,
    selectedFilter,
    residencyType,
  ]);

  return (
    <div className="p-6">
      {/* HEADER */}
      <div className="mb-6">
        <h1 className="mb-5 text-2xl font-bold">Ordered Card History</h1>
        <div className="flex flex-wrap items-center gap-4">
          {/* FILTER TYPE */}
          <select
            value={selectedFilter}
            onChange={(e) => {
              setSelectedFilter(e.target.value as FilterType);
              setSearchValue("");
              setDebouncedSearch("");
              setPagination((prev) => ({
                ...prev,
                page: 1,
              }));
            }}
            className="h-11 w-[220px] rounded-lg border border-gray-300 px-4 outline-none"
          >
            <option value="user_id">User ID</option>
            <option value="card_status">Card Status</option>
            <option value="masked_card_number">Masked Card Number</option>
            <option value="card_type">Card type</option>
          </select>
          {/* FILTER VALUE */}
          {/* FILTER VALUE */}
          {selectedFilter === "card_status" ? (
            <select
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value);
                setPagination((prev) => ({
                  ...prev,
                  page: 1,
                }));
              }}
              className="h-11 w-[220px] rounded-lg border border-gray-300 px-4 outline-none"
            >
              <option value="">Select Status</option>

              {CARD_STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          ) : selectedFilter === "card_type" ? (
            <select
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value);
                setPagination((prev) => ({
                  ...prev,
                  page: 1,
                }));
              }}
              className="h-11 w-[280px] rounded-lg border border-gray-300 px-4 outline-none"
            >
              <option value="">Select Card Type</option>

              {products.map((item: any) => (
                <option key={item.id} value={item.name}>
                  {item.name}
                </option>
              ))}
            </select>
          ) : (
            <input
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              type={selectedFilter === "user_id" ? "number" : "text"}
              placeholder={
                selectedFilter === "user_id"
                  ? "Enter User ID"
                  : "Enter Masked Card Number"
              }
              className="h-11 w-[280px] rounded-lg border border-gray-300 px-4 outline-none"
            />
          )}

          <select
            value={residencyType}
            onChange={(e) => {
              setResidencyType(e.target.value);
              setPagination((prev) => ({
                ...prev,
                page: 1,
              }));
            }}
            className="h-11 w-[220px] rounded-lg border border-gray-300 px-4 outline-none"
          >
            <option value="all">All Residency Types</option>
            <option value="RESIDENT">Resident</option>
            <option value="NON_RESIDENT">Non Resident</option>
          </select>
        </div>
      </div>

      {/* TABLE */}
      {!canViewOrderedCards ? (
        <div className="flex flex-col items-center justify-center bg-white rounded-[12px] p-8 mt-2 sm:mt-4 text-center shadow-sm">
          <p className="font-manrope text-[16px] font-semibold text-secondary">
            You do not have permission to view ordered cards.
          </p>
        </div>
      ) : (
        <div className="custom-scroll flex flex-col w-full bg-white rounded-[12px] p-4 mt-2 sm:mt-4">
          <Table
            rowKey="id"
            columns={columns}
            dataSource={tableData}
            loading={isLoading}
            scroll={{ x: true }}
            pagination={{
              current: pagination.page,
              total: pagination.total,
              pageSize: pagination.limit,
              showSizeChanger: false,
              position: ["bottomCenter"],
              onChange: (page) =>
                setPagination((prev) => ({
                  ...prev,
                  page,
                })),
            }}
          />
        </div>
      )}
    </div>
  );
};

export default VirtualOrderedCardsList;
